import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createContext, useContext, useReducer, useEffect, } from "react";
import { useForm } from "@mantine/form";
const initialState = {
    bids: [],
    opened: false,
    minimized: false,
    enableRoyalties: false,
    startTransaction: () => null,
    isTransactionRunning: false,
    errors: {},
    finished: false,
    usedTokens: {},
    coinConversion: [],
    drawerSize: 0,
};
const calculateUsedTokens = (bids) => {
    const usedTokens = {};
    bids.forEach((bid) => {
        const { chainId, address, tokenId, quantity } = bid;
        if (tokenId && quantity) {
            const id = `${address}:${tokenId}`;
            if (!usedTokens[chainId]) {
                usedTokens[chainId] = [];
            }
            const existingToken = usedTokens[chainId].find((t) => t.id === id);
            if (existingToken) {
                existingToken.quantity += quantity;
            }
            else {
                usedTokens[chainId].push({ id, quantity });
            }
        }
    });
    return usedTokens;
};
const reducer = (state, action) => {
    switch (action.type) {
        case "START_TRANSACTION":
            return { ...state, startTransaction: action.payload };
        case "ENABLE_ROYALTIES":
            return { ...state, enableRoyalties: action.payload };
        case "SET_DRAWER_SIZE":
            return { ...state, drawerSize: state.opened ? action.payload : 0 };
        case "SET_COIN_CONVERSION":
            return { ...state, coinConversion: action.payload };
        case "SET_ERROR":
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.chainId]: action.payload.error,
                },
            };
        case "SET_TRANSACTION_RUNNING":
            return { ...state, isTransactionRunning: action.payload };
        case "SET_FINISHED":
            return { ...state, finished: true };
        case "REMOVE_ALL_BIDS":
            return { ...state, bids: [], usedTokens: {} };
        case "ADD_BIDS":
            const newBids = action.payload.filter((bid) => {
                return !state.bids.find((b) => b.reservoirId === bid.reservoirId);
            });
            const mergedBids = [...state.bids, ...newBids];
            return {
                ...state,
                bids: mergedBids,
                opened: true,
                usedTokens: calculateUsedTokens(mergedBids),
            };
        case "OPEN_DRAWER":
            return { ...state, opened: true, drawerSize: 460 };
        case "MINIMIZE_DRAWER":
            return { ...state, minimized: action.payload };
        case "CLOSE_DRAWER":
            const newState = {
                ...state,
                opened: false,
                errors: {},
                usedTokens: {},
                finished: false,
                minimized: false,
                drawerSize: 0,
            };
            if (action.payload) {
                newState.bids = [];
            }
            return newState;
        case "REMOVE_BID":
            const remainingBids = state.bids.filter((bid) => bid.reservoirId !== action.payload.reservoirId);
            return {
                ...state,
                bids: remainingBids,
                usedTokens: calculateUsedTokens(remainingBids),
            };
        case "UPDATE_BID":
            const updatedBids = state.bids.map((bid) => {
                const { token, tokenId, quantity, reservoirId, royaltyBps } = action.payload;
                return bid.reservoirId === reservoirId
                    ? {
                        ...bid,
                        token: token === null ? null : token || bid.token,
                        tokenId: tokenId === null ? null : tokenId || bid.tokenId,
                        quantity: quantity === null ? 1 : quantity || 1,
                        royaltyBps: royaltyBps === null ? 0 : royaltyBps || bid.royaltyBps,
                    }
                    : bid;
            });
            return {
                ...state,
                usedTokens: calculateUsedTokens(updatedBids),
                bids: updatedBids,
            };
        case "UPDATE_ROYALTIES":
            return {
                ...state,
                bids: state.bids.map((bid) => {
                    var _a, _b;
                    const onchainId = (_b = (_a = bid.bid) === null || _a === void 0 ? void 0 : _a.collection) === null || _b === void 0 ? void 0 : _b.id;
                    const { royalties, disable } = action.payload;
                    const royalty = royalties.find((royalty) => royalty.onchainId.toLowerCase() === (onchainId === null || onchainId === void 0 ? void 0 : onchainId.toLowerCase()));
                    if (!royalty)
                        return bid;
                    const royaltyBps = disable
                        ? 0
                        : !bid.contractRoyaltyBps
                            ? royalty.royaltyBps
                            : bid.royaltyBps;
                    const royaltyRecipient = disable
                        ? undefined
                        : royalty.royaltyRecipient;
                    const contractRoyaltyBps = disable ? undefined : royalty.royaltyBps;
                    return { ...bid, contractRoyaltyBps, royaltyBps, royaltyRecipient };
                }),
            };
        default:
            return state;
    }
};
const QuicksellCartContext = createContext(null);
export const QuicksellCartProvider = ({ children, }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const form = useForm({
        initialValues: {
            tokens: state.bids,
        },
        validate: {
            tokens: {
                tokenId: (value) => (!value ? "Token ID is required" : null),
                chainId: (value) => (!value ? "Chain ID is required" : null),
                address: (value) => (!value ? "Address is required" : null),
                quantity: (value) => value <= 0 ? "Quantity must be greater than 0" : null,
            },
        },
    });
    useEffect(() => {
        form.setFieldValue("tokens", state.bids);
    }, [state.bids]);
    return (_jsx(QuicksellCartContext.Provider, { value: { state, dispatch, form }, children: children }));
};
export const useQuicksellCartContext = () => {
    const context = useContext(QuicksellCartContext);
    if (!context) {
        throw new Error("useListingCartContext must be used within a ListingCartProvider");
    }
    return context;
};
